import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import PWAInstallButton from '../components/PWAInstallButton'
import { SipSearch } from '../components/form/SipControls'

function Header({ setSidebarOpen, sidebarOpen }) {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const { currentUser, logout } = useAuth()
  const navigate = useNavigate()
  const dropdownRef = useRef(null)

  const handleLogout = () => {
    logout()
    navigate('/login')
    setDropdownOpen(false)
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-secondary pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]">
      <div className="px-3 sm:px-4 tablet:px-5 md:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between min-h-[3.5rem]">
          {/* Left side - Menu buttons */}
          <div className="flex items-center">
            <button
              type="button"
              className="-ml-1 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center text-slate-600 hover:text-[#6E63F6] rounded-xl hover:bg-primary/5 transition-colors"
              onClick={(event) => {
                event.stopPropagation();
                setSidebarOpen((open) => !open);
              }}
              aria-pressed={sidebarOpen}
            >
              <span className="sr-only">
                {sidebarOpen ? "Sembunyikan menu" : "Tampilkan menu"}
              </span>
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
              </svg>
            </button>
          </div>

          {/* Search Bar - tampil dari tablet/iPad mini ke atas */}
          <div className="hidden tablet:flex flex-1 max-w-md mx-4 md:mx-6">
            <SipSearch className="w-full" placeholder="Cari..." />
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Notifications */}
            <button
              type="button"
              className="relative p-2 text-neutral-500 hover:text-primary rounded-lg hover:bg-secondary transition-colors"
            >
              <span className="sr-only">Lihat notifikasi</span>
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
              </svg>
              {/* Notification badge */}
              <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-accent">
              </span>
            </button>

            {/* PWA Install Button */}
            <PWAInstallButton />

            {/* Separator */}
            <div className="hidden lg:block h-5 w-px bg-secondary" />

            {/* Profile dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                className="flex items-center gap-2 text-sm rounded-lg py-1 px-2 hover:bg-secondary transition-colors"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <div className="h-7 w-7 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-white font-semibold text-xs">
                    {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <span className="hidden lg:flex lg:items-center">
                  <span className="text-sm font-medium text-dark">
                    {currentUser?.name || 'User'}
                  </span>
                  <svg className={`ml-1.5 h-3.5 w-3.5 text-neutral-400 transition-transform duration-150 ${dropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                  </svg>
                </span>
              </button>

              {/* Dropdown menu */}
              {dropdownOpen && (
                <div className="absolute right-0 z-50 mt-2 w-52 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-secondary border border-secondary">
                  <div className="p-1.5">
                    {/* User info */}
                    <div className="px-3 py-2 border-b border-secondary mb-1">
                      <p className="text-sm font-medium text-dark">{currentUser?.name || 'User'}</p>
                      <p className="text-xs text-neutral-500">{currentUser?.email || 'user@example.com'}</p>
                    </div>
                    
                    {/* Menu items */}
                    <button
                      onClick={() => setDropdownOpen(false)}
                      className="flex w-full items-center px-3 py-2 text-sm text-neutral-700 hover:bg-secondary hover:text-primary rounded-lg transition-colors"
                    >
                      <svg className="mr-2.5 h-4 w-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Profil Saya
                    </button>
                    <button
                      onClick={() => setDropdownOpen(false)}
                      className="flex w-full items-center px-3 py-2 text-sm text-neutral-700 hover:bg-secondary hover:text-primary rounded-lg transition-colors"
                    >
                      <svg className="mr-2.5 h-4 w-4 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Pengaturan
                    </button>
                    <div className="border-t border-secondary my-1"></div>
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center px-3 py-2 text-sm text-danger hover:bg-danger/5 rounded-lg transition-colors"
                    >
                      <svg className="mr-2.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Keluar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Search Bar */}
      <div className="tablet:hidden px-4 pb-3">
        <SipSearch placeholder="Cari..." />
      </div>
    </header>
  )
}

export default Header